import Spinner from "../../shared/UxbiSpinner";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Button, Form, Modal, Pagination, Table } from "react-bootstrap";
import { Search } from "lucide-react";
import { toast } from "react-toastify";
import {
  listarDocumentosPrevios,
  obtenerDocumentoPrevio,
} from "../../api/documentos";
import { money, normalizePage } from "./documentoUtils";

const LIMIT = 25;

export default function DocumentoPrevioSelector({
  show,
  config,
  configuration,
  idEmpresa,
  onCancel,
  onSelect,
}) {
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({
    data: [],
    current_page: 1,
    last_page: 1,
  });
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);

  const generation = useRef(0);
  const load = useCallback(async () => {
    const current = ++generation.current;
    if (!show || !configuration?.id) return;
    setLoading(true);
    try {
      const response = await listarDocumentosPrevios({
        idDocumentoPosterior: configuration.id,
        idEmpresa,
        search: appliedSearch,
        page,
        limit: LIMIT,
      });
      if (current === generation.current) setResult(normalizePage(response));
    } catch (err) {
      if (current === generation.current) toast.error(err.message);
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, [appliedSearch, configuration?.id, idEmpresa, page, show]);

  useEffect(() => {
    load();
    const current = generation.current;
    return () => {
      if (generation.current === current) generation.current = current + 1;
    };
  }, [load]);

  const applySearch = (event) => {
    event.preventDefault();
    setPage(1);
    setSelected({});
    setAppliedSearch(search.trim());
  };

  const multiple = Boolean(config.permiteMultiplesPrevios);
  const selectedIds = useMemo(
    () => Object.keys(selected).filter((id) => selected[id]),
    [selected],
  );

  const toggle = (id) => {
    const key = String(id);
    setSelected((current) =>
      multiple ? { ...current, [key]: !current[key] } : { [key]: true },
    );
  };

  const choose = async (documentId = null) => {
    const ids = documentId ? [String(documentId)] : selectedIds;
    if (!ids.length) return;
    setSelecting(true);
    try {
      const documentos = await Promise.all(
        ids.map((id) => obtenerDocumentoPrevio(id)),
      );
      if (documentos.some((documento) => !documento))
        throw new Error(
          "No fue posible cargar uno de los documentos previos seleccionados.",
        );
      await onSelect(multiple ? documentos : documentos[0]);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSelecting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onCancel}
      size="xl"
      fullscreen="md-down"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>Seleccionar documento previo</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-secondary mb-3">
          Selecciona {multiple ? "uno o varios documentos" : "el documento"} a
          partir del cual se creará {config.singular.toLowerCase()}. Sólo
          aparecen documentos con partidas pendientes y que no están bloqueados.
        </p>
        <Form onSubmit={applySearch} className="d-flex gap-2 mb-3">
          <Form.Control
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por folio, cliente, referencia o persona que solicita"
          />
          <Button type="submit" variant="outline-primary">
            <Search size={17} /> Buscar
          </Button>
        </Form>

        {loading ? (
          <div className="text-center py-5">
            <Spinner />
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="align-middle">
              <thead>
                <tr>
                  <th style={{ width: 42 }} />
                  <th>Folio</th>
                  <th>Fecha</th>
                  <th>Cliente</th>
                  <th>Referencia</th>
                  <th className="text-end">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {result.data.length ? (
                  result.data.map((documento) => (
                    <tr
                      key={documento.id}
                      role="button"
                      className={
                        selected[String(documento.id)] ? "table-active" : ""
                      }
                      onClick={() => toggle(documento.id)}
                      onDoubleClick={() => !multiple && choose(documento.id)}
                    >
                      <td>
                        <Form.Check
                          type={multiple ? "checkbox" : "radio"}
                          name="documento_previo"
                          checked={Boolean(selected[String(documento.id)])}
                          onChange={() => toggle(documento.id)}
                          onClick={(event) => event.stopPropagation()}
                        />
                      </td>
                      <td>
                        <strong>{documento.folio || `#${documento.id}`}</strong>
                      </td>
                      <td>{formatDate(documento.fecha)}</td>
                      <td>
                        {documento.cliente?.nombre ||
                          documento.nombre_cliente ||
                          "—"}
                      </td>
                      <td>{documento.referencia || "—"}</td>
                      <td className="text-end">
                        {money(
                          documento.saldo_documento ??
                            documento.saldo ??
                            documento.importe,
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center text-secondary py-4">
                      No hay documentos previos disponibles.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}

        {result.last_page > 1 && (
          <Pagination className="justify-content-end mb-0">
            <Pagination.Prev
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
            />
            <Pagination.Item active>{result.current_page}</Pagination.Item>
            <Pagination.Next
              disabled={page >= result.last_page}
              onClick={() => setPage((current) => current + 1)}
            />
          </Pagination>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={() => choose()}
          disabled={!selectedIds.length || selecting}
        >
          {selecting ? <Spinner size="sm" /> : null} Usar{" "}
          {multiple
            ? `${selectedIds.length} documento(s)`
            : "documento seleccionado"}
        </Button>
        <Button variant="outline-secondary" onClick={onCancel}>
          Cancelar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("es-MX");
}
